
import { ChargeTypePolicy } from "constants/charge/ChargeTypePolicy";
import { SubChargeTypePolicy } from "constants/charge/SubChargeTypePolicy";
import { ParameterCodeDataPolicy } from "constants/common/ParameterCodeDataPolicy";
import { ResponseEntity } from "domain/entity/Common/ResponseEntity";
import { MasterDataType } from "domain/entity/MasterData/MasterDataEntity";
import { EMPTY_MASTER_FSP_ENTITY, MasterFSPEntity } from "domain/entity/MasterFSP/MasterFSPEntity";
import { ConsortiumRepository } from "domain/repository/ChargeDetailMaintenance/ConsortiumRepo";
import { ChargeTypeRepository } from "domain/repository/ChargeType/ChargeTypeRepo";
import { CompanyRepository } from "domain/repository/Company/CompanyRepo";
import { FspDirectionRefRepository } from "domain/repository/FspDirectionRef/FspDirectionRefRepo";
import { MasterDataRepository } from "domain/repository/MasterData/MasterDataRepo";
import { MasterFSPRepository } from "domain/repository/MasterFSP/MasterFSPRepo";
import { ParameterDetailRepository } from "domain/repository/parameter/ParameterDetailRepo";
import _ from "lodash";
import { FspCalculateMethodDroOpts } from "presentation/constant/DropDownOptions/Common/FspCalculateMethodDroOpts";
import { SplitDaysToDroOpts } from "presentation/constant/DropDownOptions/Common/SplitDaysToDroOpts";
import { CntrMovementDroOpts } from "presentation/constant/DropDownOptions/Company/CntrMovementDroOpts";
import { CntrStatusDroOpts } from "presentation/constant/DropDownOptions/Company/CntrStatusDroOpts";
import { CustomerTypeDroOpts } from "presentation/constant/DropDownOptions/Company/CustomerTypeDroOpts";
import { EventTypeDroOpts } from "presentation/constant/DropDownOptions/Company/EventTypeDroOpts";
import { FSPCountingDroOpts } from "presentation/constant/DropDownOptions/Company/FSPCountingDroOpts";
import { FSPUomDroOpts } from "presentation/constant/DropDownOptions/Company/FSPUomDroOpts";
import { masterFSPMaintenanceRequiredFieldList } from "presentation/constant/MasterFSP/MasterFSPMaintenanceConstant";
import { DropdownProps } from "presentation/model/DropdownProps";
import { MasterFSPMaintenanceModel } from "presentation/model/MasterFSP/MasterFSPMaintenanceModel";
import { Dispatch, SetStateAction } from "react";
import { IFieldValue } from "veronica-ui-component/dist/component/core";
import BaseViewModel from "../BaseViewModel";

interface MasterFSPMaintenanceVMProps extends BaseViewModel {
    dispatch: [
        Dispatch<SetStateAction<MasterFSPMaintenanceModel>> | ((value: SetStateAction<MasterFSPMaintenanceModel>) => void),
    ],
    repo: MasterFSPRepository,
    parameterDetailRepo: ParameterDetailRepository,
    companyRepo: CompanyRepository,
    consortiumRepo: ConsortiumRepository,
    masterDataRepo: MasterDataRepository,
    chargeTypeRepo: ChargeTypeRepository,
    fspDirectionRefRepo: FspDirectionRefRepository,
}

export const MasterFSPMaintenanceVM = ({ dispatch, repo, companyRepo, consortiumRepo, masterDataRepo, fspDirectionRefRepo,
    chargeTypeRepo, parameterDetailRepo }: MasterFSPMaintenanceVMProps) => {
    const [masterFSPMainDispatch] = dispatch;

    const onShowLoading = () => {
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                isLoading: true,
            }
        })
    }

    const onHideLoading = () => {
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                isLoading: false,
            }
        })
    }
    const loadDropdownOption = async (operatingUnit: string) => {
        masterFSPMainDispatch(prevState => ({
            ...prevState,
            dynamicOptions: {
                ...prevState.dynamicOptions,
                calendarAdjDropdownOptions: EventTypeDroOpts().getEventTypeModel(),
                customerTypeDropdownOptions: CustomerTypeDroOpts().getCustomerTypeModel(),
                splitDaysToDropdownOptions: SplitDaysToDroOpts().getDroOptsModel(),
                //(operatingUnit === 'SAPT' || operatingUnit === 'KICT') ? FSPUomDroOpts().getFSPUomModelForSAPT() : FSPUomDroOpts().getFSPUomModel()
                //fspUomDropdownOptions: FSPUomDroOpts(operatingUnit).getAllFspUomModel(),
                //fspCalculateMethodDropdownOptions: FspCalculateMethodDroOpts().getDroOptsModel("", ""),
                //cntrMovementDropdownOptions: CntrMovementDroOpts().getDroOptsModel(true),
                //cntrStatusDropdownOptions: CntrStatusDroOpts().getDroOptsModelByMovementAndChgType("", ""),
            }
        }))
        await companyRepo.getAllCompanyForCombobox().then(
            companyCodeList => {
                const activeCompanyCode = companyCodeList?.filter((compCode) => {
                    return compCode.customerType === 'SL' && compCode.activeInd === 'Y'
                        && (compCode.companyType === 'ALLIANCE' || (compCode.companyType !== 'ALLIANCE' && compCode.status === 'CONFIRMED'));
                });
                const shippingLineCompanyDropdownOptions = activeCompanyCode?.map((companyEty) => ({
                    dropdownLabel: companyEty.companyCode,
                    tagLabel: companyEty.companyCode,
                    value: companyEty.companyCode,
                })) ?? []

                masterFSPMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        shippingLineCompanyDropdownOptions: shippingLineCompanyDropdownOptions
                    }
                }))
            }
        )

        await consortiumRepo.getAllConsortiums().then(
            consortiums => {
                let consortiumCodeDropdownOptions = consortiums?.map((item) => ({
                    dropdownLabel: item.consortiumCode,
                    tagLabel: item.consortiumCode,
                    value: item.consortiumCode,
                })) ?? []
                consortiumCodeDropdownOptions = _.orderBy(consortiumCodeDropdownOptions, "dropdownLabel");

                masterFSPMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        consortiumCodeDropdownOptions: consortiumCodeDropdownOptions,
                    }
                }))
            }
        );

        await masterDataRepo.getMasterDataByKey(MasterDataType.SERVICE).then(
            serviceCodes => {
                const serviceCodeDropdownOptions = serviceCodes?.map((serviceCode) => ({
                    dropdownLabel: serviceCode.code,
                    tagLabel: serviceCode.code,
                    value: serviceCode.code,
                })) ?? []

                masterFSPMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        serviceCodeDropdownOptions: serviceCodeDropdownOptions
                    }
                }))
            }
        );

        await chargeTypeRepo.getAllChargeTypesForCombobox().then(
            chargeTypes => {
                let newChargeTypeList = _.orderBy(chargeTypes, ["chargeType", "subChargeType"]);
                let newChargeTypes = newChargeTypeList.filter(entity => entity.countFspInd === 'Y');

                let chargeTypeDropdownOption: DropdownProps[] = [];
                let subChargeTypeDropdownOption: { [key: string]: DropdownProps[] } = {};
                newChargeTypes?.forEach(chgTypeEty => {
                    const chgTypeExisted = chargeTypeDropdownOption?.find(chgType =>
                        chgType.value === chgTypeEty.chargeType);
                    if (!chgTypeExisted) {
                        chargeTypeDropdownOption.push({
                            dropdownLabel: chgTypeEty.chargeType,
                            tagLabel: chgTypeEty.chargeType,
                            value: chgTypeEty.chargeType
                        })
                    }
                    if (chgTypeEty.subChargeType && chgTypeEty.activeInd === 'Y' && chgTypeEty.chargeCategory !== 'USER' && chgTypeEty.subChargeType !== 'T2'
                        && chgTypeEty.subChargeType !== 'PCIH' && chgTypeEty.subChargeType !== 'PCXH'
                    ) {
                        if (!subChargeTypeDropdownOption[chgTypeEty.chargeType]) {
                            subChargeTypeDropdownOption[chgTypeEty.chargeType] = [];
                        }
                        subChargeTypeDropdownOption[chgTypeEty.chargeType].push({
                            dropdownLabel: chgTypeEty.subChargeType,
                            tagLabel: chgTypeEty.subChargeType,
                            value: chgTypeEty.subChargeType
                        });
                    }
                });

                masterFSPMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        chargeTypeDropdownOptions: chargeTypeDropdownOption,
                        subChargeTypeDropdownOptions: subChargeTypeDropdownOption
                    }
                }))
            }
        )

        await parameterDetailRepo.getAllParameterDtlsByParameterCode(ParameterCodeDataPolicy.SHIPMENT_TYPE).then(
            (shipmentTypeList) => {
                const shipmentTypeDropdownOption = shipmentTypeList?.map((shipmentType) => ({
                    dropdownLabel: shipmentType.parameterDtlCode,
                    tagLabel: shipmentType.parameterDtlCode,
                    value: shipmentType.parameterDtlCode,
                })) ?? []

                masterFSPMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        shipmentTypeDropdownOptions: shipmentTypeDropdownOption
                    }
                }))
            }
        )

        await parameterDetailRepo.getAllParameterDtlsByParameterCode(ParameterCodeDataPolicy.CARGO_CODE).then(
            (cargeCodeList) => {
                const cargeCodeDropdownOption = cargeCodeList?.map((cargeCode) => ({
                    dropdownLabel: cargeCode.parameterDtlCode,
                    tagLabel: cargeCode.parameterDtlCode,
                    value: cargeCode.parameterDtlCode,
                })) ?? []

                masterFSPMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        cargeCodeDropdownOption: cargeCodeDropdownOption
                    }
                }))
            }
        )

        await masterDataRepo.getMasterDataByKey(MasterDataType.FORWARDER_CODE).then(
            forwarderCodes => {
                const forwarderCodesDropdownOptions = forwarderCodes?.map((forwarderCode) => ({
                    dropdownLabel: forwarderCode.code,
                    tagLabel: forwarderCode.code,
                    value: forwarderCode.code,
                })) ?? []

                masterFSPMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        forwarderCodeDropdownOptions: forwarderCodesDropdownOptions,
                    }
                }))
            }
        )

        await repo.getLoadAgentCode().then(
            agentInfos => {
                const agentInfosDropdownOptions = agentInfos?.map((agentInfo) => ({
                    dropdownLabel: agentInfo.agentCode || '',
                    tagLabel: agentInfo.agentCode || '',
                    value: agentInfo.agentCode || '',
                })) ?? []

                masterFSPMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        agentInfosDropdownOptions: agentInfosDropdownOptions,
                    }
                }))
            }
        )

        await fspDirectionRefRepo.getAllFspDirectionRefComboBox().then(
            fspDirections => {
                let newFspDirectionList = _.orderBy(fspDirections, ["chargeType", "subChargeType"]);
                let newFspDirections = newFspDirectionList.filter(entity => entity.activeInd === 'Y');

                let fspDirectionDropOpts: { [key: string]: DropdownProps[] } = {};
                newFspDirections?.forEach(fspDirection => {
                    let dropOptKey = (fspDirection.chargeType ?? "") + "_" + (fspDirection.subChargeType ?? "");

                    if (!fspDirectionDropOpts[dropOptKey]) {
                        fspDirectionDropOpts[dropOptKey] = [];
                    }
                    fspDirectionDropOpts[dropOptKey].push({
                        dropdownLabel: fspDirection.direction,
                        tagLabel: fspDirection.direction,
                        value: fspDirection.direction
                    });

                });

                masterFSPMainDispatch(prevState => ({
                    ...prevState,
                    dynamicOptions: {
                        ...prevState.dynamicOptions,
                        directionDropdownOptions: fspDirectionDropOpts,
                    }
                }))
            }
        )
    }

    const reLoadDropdownOption = (operatingUnit: string) => {
        masterFSPMainDispatch(prevState => {
            const chargeTypeStr = prevState.masterState.editingEntity?.chargeType;
            const subChargeTypeStr = prevState.masterState.editingEntity?.subChargeType;

            const chargeTypeDropOpts = prevState.dynamicOptions.chargeTypeDropdownOptions;

            const chargeType = chargeTypeStr ? chargeTypeStr : (chargeTypeDropOpts && chargeTypeDropOpts.length > 0 ? chargeTypeDropOpts[0].value : "");
            const subChargeTypeDropOpts = chargeType ? prevState.dynamicOptions.subChargeTypeDropdownOptions[chargeType] : [];

            const subChargeType = subChargeTypeStr ? subChargeTypeStr : (subChargeTypeDropOpts && subChargeTypeDropOpts.length > 0 ? subChargeTypeDropOpts[0].value : "");

            prevState = initDropOptsByChg(prevState, chargeType, subChargeType, operatingUnit);

            return {
                ...prevState,
                forceRefresh: !prevState.forceRefresh
            }
        })
    }

    /*const onChargeTypeChange = async (fieldValue: IFieldValue, subChargeType: string, movement: string, operatingUnit: string) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }
        const newChargeType = getChargeTypeForCC(val, subChargeType ?? '')
        const cntrMovementDroOpts = initCntrMovement(newChargeType);
        const cntrStatusDroOpts = initCntrStatus(newChargeType, movement ?? "");
        const fspCountingFromDroOpts = initFspCountingFrom(newChargeType);
        const fspCountingToDroOpts = initFspCountingTo(newChargeType, operatingUnit);
        // const directionDroOpts = initDirection(newChargeType);
        let directionDropdownOption: DropdownProps[] = [];
        const data = await fspDirectionRefRepo.getAllFspDirectionRefComboBox();
        const newData = data.filter(entity => !entity.chargeType || entity.chargeType === newChargeType);

        newData?.forEach(entity => {
            directionDropdownOption.push({
                dropdownLabel: entity.direction,
                tagLabel: entity.direction,
                value: entity.direction
            })
        })

        masterFSPMainDispatch(prevState => ({
            ...prevState,
            dynamicOptions: {
                ...prevState.dynamicOptions,
                cntrMovementDropdownOptions: cntrMovementDroOpts,
                cntrStatusDropdownOptions: cntrStatusDroOpts,
                fspCountingFromDropdownOptions: fspCountingFromDroOpts,
                fspCountingToDropdownOptions: fspCountingToDroOpts,
                directionDropdownOptions: directionDropdownOption,
            },
            masterState: {
                ...prevState.masterState,
                editingEntity: {
                    ...prevState.masterState.editingEntity,
                    // status: 'NEW',
                    // chargeType: chgType,
                    // subChargeType: subChgType,
                    // fspUOM: FSPUomDroOpts().getFSPUomNoHfDayModelForSAPT()[0].value,
                    // fspCalculateMethod: FspCalculateMethodDroOpts().getDroOptsModel("", "")[0].value,
                    //fspCountingFrom: newChargeType ? (FSPCountingDroOpts().getFSPCountingFromByChargeType(newChargeType).length > 0 ? FSPCountingDroOpts().getFSPCountingFromByChargeType(newChargeType)[0].value : '') : '',
                    //fspCountingTo: newChargeType ? (FSPCountingDroOpts().getFSPCountingToByChargeType(newChargeType, operatingUnit).length > 0 ? FSPCountingDroOpts().getFSPCountingToByChargeType(newChargeType, operatingUnit)[0].value : '') : '',
                },
            }
        }))
    }*/

    const onSubChargeTypeChange = (fieldKey: string, chgType: string, fieldValue: string, operatingUnit: string) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        const subChgType = val;
        masterFSPMainDispatch(prevState => {
            prevState = initDropOptsByChg(prevState, chgType, subChgType, operatingUnit);
            prevState.masterState.editingEntity.subChargeType = subChgType;
            prevState = onInitFormValue(prevState);

            return {
                ...prevState,
                forceRefresh: !prevState.forceRefresh
            }
        })
    }

    const initCntrMovement = (chargeType: string) => {
        if (chargeType === ChargeTypePolicy.CHARGE_TYPE_ITM
            || chargeType === ChargeTypePolicy.CHARGE_TYPE_OBS
            || chargeType === ChargeTypePolicy.CHARGE_TYPE_TSS
        ) {
            return CntrMovementDroOpts().getDroOptsModel(false);
        } else {
            return CntrMovementDroOpts().getDroOptsModel(true);
        }
    }

    const getChargeTypeForCC = (chargeType: string, subChargeType: string) => {
        let newChargeType;

        if (chargeType && chargeType === ChargeTypePolicy.CHARGE_TYPE_CC) {
            if (subChargeType === ChargeTypePolicy.CHARGE_TYPE_OBS) {
                newChargeType = ChargeTypePolicy.CHARGE_TYPE_OBS;
            } else if (subChargeType === SubChargeTypePolicy.CODE_ITM) {
                newChargeType = ChargeTypePolicy.CHARGE_TYPE_ITM;
            } else {
                newChargeType = chargeType;
            }
        } else if (chargeType && chargeType === ChargeTypePolicy.CHARGE_TYPE_DEM) {
            if (subChargeType === ChargeTypePolicy.CHARGE_TYPE_DEX) {
                newChargeType = ChargeTypePolicy.CHARGE_TYPE_OBS;
            } else if (subChargeType === ChargeTypePolicy.CHARGE_TYPE_DEI) {
                newChargeType = ChargeTypePolicy.CHARGE_TYPE_ITM;
            } else {
                newChargeType = chargeType;
            }
        } else {
            newChargeType = chargeType;
        }

        return newChargeType;
    }

    const initCntrStatus = (chargeType: string, cntrMovement: string, operatingUnit: string) => {
        return CntrStatusDroOpts().getDroOptsModelByMovementAndChgType(chargeType, cntrMovement, operatingUnit);
    }

    const initFspCountingFrom = (chargeType: string, operatingUnit: string) => {
        return FSPCountingDroOpts(operatingUnit).getFSPCountingFromByChargeType(chargeType);
    }

    const initFspCountingTo = (chargeType: string, operatingUnit: string) => {
        return FSPCountingDroOpts(operatingUnit).getFSPCountingToByChargeType(chargeType/*, operatingUnit*/);
    }

    const initFspCountingFromBySubChgType = (subChargeType: string, operatingUnit: string) => {
        return FSPCountingDroOpts(operatingUnit).getFSPCountingFromBySubChargeType(subChargeType);
    }

    const initFspCountingToBySubChgType = (subChargeType: string, operatingUnit: string) => {
        return FSPCountingDroOpts(operatingUnit).getFSPCountingToBySubChargeType(subChargeType);
    }

    const onSearch = async () => {
        const entities = await repo.getEntities();

        if (entities && entities.length > 0) {
            let isRejectedCount = 0;
            let isPendingApprovalCount = 0;
            isRejectedCount = entities.filter(entity => entity.status === "NEW" && entity.rejectReason).length;
            isPendingApprovalCount = entities.filter(entity => (entity.status === "FINALIZED") && !entity.confirmedDate).length;
            masterFSPMainDispatch(prevState => {
                return {
                    ...prevState,
                    allData: entities,
                    isPendingApprovalCount: isPendingApprovalCount,
                    isRejectedCount: isRejectedCount,
                    selectedRows: [],
                    isAllowAutoSearch: false,
                    isBackMaster: false,
                }
            })
        } else {
            masterFSPMainDispatch(prevState => {
                return {
                    ...prevState,
                    allData: [],
                    isPendingApproval: false,
                    isRejected: false,
                    isPendingApprovalCount: 0,
                    isRejectedCount: 0,
                }
            })
        }
    }

    const onSearchCriteriaResetClick = () => {
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: EMPTY_MASTER_FSP_ENTITY
            }
        })
    }

    const searchMasterFSP = async (searchCriteria: MasterFSPEntity) => {
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                selectedRows: [],
                tableData: [],
                isPendingApproval: false,
                isRejected: false,
                forceRefresh: !prevState.forceRefresh,
                currentSelectedRow: { ...EMPTY_MASTER_FSP_ENTITY },

                isShowRightPanel: false,
                isShowDeleteModal: false,
                isAllowAutoSearch: false,

                masterState: {
                    isSliderOpen: false,
                    isTabularDataActive: false,
                    isAdd: false,
                    isRead: false,
                    isEditable: false,
                    isSaveClicked: false,
                    lastEditRowId: "",
                    isSelected: false,
                    slider: {
                        isAdd: false,
                        isRead: false,
                        isEditable: false
                    },
                    editingEntity: EMPTY_MASTER_FSP_ENTITY
                }
            }
        })
        await repo.searchMasterFSP(searchCriteria).then((data) => {
            if (data) {
                masterFSPMainDispatch(prevState => {
                    return {
                        ...prevState,
                        tableData: data,
                        searchCriteria: searchCriteria,
                        selectedRows: [],
                        isAllowAutoSearch: false,
                        searchStatus: '',
                        isSearch: !prevState.isSearch,
                    }
                })
            } else {
                masterFSPMainDispatch(prevState => {
                    return {
                        ...prevState,
                        tableData: [],
                        searchCriteria: searchCriteria,
                        selectedRows: [],
                        isAllowAutoSearch: false,
                        searchStatus: '',
                        isSearch: !prevState.isSearch,
                    }
                })
            }
        }).catch((error) => {
            return [];
        }).finally(
            () => onCountRejectedPendingApproval(),
        );
    }


    const onAdd = (/*chargeTypeDropdownOptions: DropdownProps[], subChargeTypeDropdownOptions: { [key: string]: DropdownProps[] }, operatingUnit: string*/) => {
        /*const chgType = chargeTypeDropdownOptions.length > 0 ? chargeTypeDropdownOptions[0].value : '';
        const subChgType = chgType ? (subChargeTypeDropdownOptions[chgType] ? subChargeTypeDropdownOptions[chgType][0].value : '') : '';
        const newChgType = getChargeTypeForCC(chgType, subChgType);
        
        const fspCountingFromDroOpts = initFspCountingFrom(newChgType, operatingUnit);
        const fspCountingToDroOpts = initFspCountingTo(newChgType, operatingUnit);*/

        masterFSPMainDispatch(prevState => {
            /*const directionKey = chgType + "_" + subChgType;
            let directionDropdownOption = prevState.dynamicOptions.directionDropdownOptions[directionKey];*/

            return {
                ...prevState,
                isShowEditPanel: true,
                masterState: {
                    ...prevState.masterState,
                    isAdd: true,
                    isEditable: false,
                    isRead: false,
                    editingEntity: {
                        ...EMPTY_MASTER_FSP_ENTITY,
                        status: 'NEW',
                    },
                }
            }
        })
    }

    const onEdit = (currentEntity: MasterFSPEntity, operatingUnit: string) => {
        /*onChargeTypeChange(currentEntity.chargeType, '', '', operatingUnit);
        onSubChargeTypeChange(currentEntity.chargeType ?? '', currentEntity.subChargeType);*/
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                currentSelectedRow: currentEntity,
                isShowEditPanel: true,
                masterState: {
                    ...prevState.masterState,
                    isAdd: false,
                    isEditable: false,
                    isRead: true,
                    editingEntity: {
                        ...currentEntity
                    },
                }
            }
        })
    }

    const onEditClick = (currentEntity: MasterFSPEntity) => {
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                currentSelectedRow: currentEntity,
                isShowEditPanel: true,
                masterState: {
                    ...prevState.masterState,
                    isAdd: false,
                    isEditable: true,
                    isRead: currentEntity.masterType === 'Country Code' || currentEntity.status === 'FINALIZED',
                    editingEntity: {
                        ...currentEntity
                    },
                }
            }
        })
    }

    const onReset = () => {
        masterFSPMainDispatch(prevState => {
            const resetEntity = prevState.masterState.isAdd ? EMPTY_MASTER_FSP_ENTITY : prevState.currentSelectedRow;
            return {
                ...prevState,
                masterState: {
                    ...prevState.masterState,
                    editingEntity: {
                        ...resetEntity
                    },
                }
            }
        })
    }

    const onClose = () => {
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                currentSelectedRow: EMPTY_MASTER_FSP_ENTITY,
                selectedRows: [],
                masterState: {
                    ...prevState.masterState,
                    isAdd: false,
                    isEditable: false,
                    isRead: true,
                    editingEntity: EMPTY_MASTER_FSP_ENTITY,
                    allFormState: {}
                },
                isBackMaster: true,
                isShowEditPanel: false,
            }
        })
    }

    const onSaveClicked = () => {
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                masterState: {
                    ...prevState.masterState,
                    isSaveClicked: true,
                    allFormState: {},
                }
            }
        })
    }

    const onSave = async (currentEntity: MasterFSPEntity, isAdd: boolean) => {
        // const valResult = isAdd ? await Validation(createMasterFSPMaintenanceValidationSchema).ValidateFormOnly(currentEntity) : null;

        const valResult = await validation(currentEntity);

        let validatedResult: { [x: string]: string } = {};
        if (!_.isEmpty(valResult)) {
            validatedResult = { ...valResult };

            masterFSPMainDispatch(prevState => {
                return {
                    ...prevState,
                    masterState: {
                        ...prevState.masterState,
                        allFormState: {
                            ...validatedResult
                        },
                    }
                }
            });

            const res: ResponseEntity = {
                code: "",
                success: false,
                msg: null,
                data: null
            }

            return res;
        }

        if (isAdd) {
            return await repo.createEntity(currentEntity);
        } else {
            return await repo.updateEntity(currentEntity);
        }
    }

    const validation = async (currentEntity: MasterFSPEntity) => {
        let validatedResult: { [x: string]: string } = {};

        // Mandatory check
        Object.entries(masterFSPMaintenanceRequiredFieldList).forEach(([field, name]) => {
            if (!currentEntity[field] && !(field === 'fspDays' && currentEntity[field] === 0)) {
                validatedResult[field] = `Missing.`;
            }
        });

        if (currentEntity.effectiveDateTo && currentEntity.effectiveDateFrom && new Date(currentEntity.effectiveDateTo) < new Date(currentEntity.effectiveDateFrom)) {
            validatedResult['effectiveDateFrom'] = "The Effective Date To must be greater than the Effective Date From.";
        }

        if (currentEntity.fspCalculateMethod && currentEntity.fspCalculateMethod === 'By Splitting Day') {
            if (currentEntity.splitFirstDays === null) {
                // validatedResult['splitFirstDays'] = `${MasterFSPMaintenanceConstant.SPLIT_FIRST_DAYS} is mandatory.`;
                validatedResult['splitFirstDays'] = `Missing.`;
            }
            if (!currentEntity.splitFirstDaysTo) {
                // validatedResult['splitFirstDaysTo'] = `${MasterFSPMaintenanceConstant.SPLIT_FIRST_DAYS_TO} is mandatory.`;
                validatedResult['splitFirstDaysTo'] = `Missing.`;
            }
            if (!currentEntity.splitRemainDaysTo) {
                // validatedResult['splitRemainDaysTo'] = `${MasterFSPMaintenanceConstant.SPLIT_REMAIN_DAYS_TO} is mandatory.`;
                validatedResult['splitRemainDaysTo'] = `Missing.`;
            }
            if (currentEntity.splitFirstDays && (currentEntity.splitFirstDays < 0 || currentEntity.splitFirstDays > 10000)) {
                validatedResult['splitRemainDaysTo'] = 'Split First Days Input Value should be range from 0 to 10000.';
            }
        }

        const fields = [currentEntity.graceHourFrom, currentEntity.graceHourTo, currentEntity.graceMinuteFrom, currentEntity.graceMinuteTo];
        const hasSome = fields.some(f => f !== null);
        const hasAll = fields.every(f => f !== null);

        if (hasSome && !hasAll) {

            validatedResult['graceHourFrom'] = 'Grace hour and Grace minute should be input together.';
        }
        if (currentEntity.fspUOM === 'HFDAY' && currentEntity.fspDays && currentEntity.fspDays > 1) {

            validatedResult['graceHourFrom'] = 'FSP Days Input Value should be range from 0 to 1 when UOM is HFDAY.';
        }
        if (currentEntity.graceHourFrom && currentEntity.graceHourFrom > 23) {
            validatedResult['graceHourFrom'] = 'Grace Hour Count From Input Value should be range from 0 to 23.';
        }
        if (currentEntity.graceHourTo && currentEntity.graceHourTo > 23) {
            validatedResult['graceHourFrom'] = 'Grace Hour Count To Input Value should be range from 0 to 23.';
        }
        if (currentEntity.graceMinuteFrom && currentEntity.graceMinuteFrom > 59) {
            validatedResult['graceHourFrom'] = 'Grace Minute Count From Input Value should be range from 0 to 59.';
        }
        if (currentEntity.graceMinuteTo && currentEntity.graceMinuteTo > 59) {
            validatedResult['graceHourFrom'] = 'Grace Minute Count To Input Value should be range from 0 to 59.';
        }

        return validatedResult;
    }

    const onCheckboxChange = (checked: boolean, fieldName: string) => {
        masterFSPMainDispatch(prevState => ({
            ...prevState,
            masterState: {
                ...prevState.masterState,
                editingEntity: {
                    ...prevState.masterState.editingEntity,
                    [fieldName]: checked ? "Y" : "N",
                }
            }
        }))
    }

    const refershSubChargeType = (chargeType: string, operatingUnit: string) => {
        masterFSPMainDispatch(prevState => ({
            ...prevState,
            masterState: {
                ...prevState.masterState,
                editingEntity: {
                    ...prevState.masterState.editingEntity,
                    subChargeType: prevState.dynamicOptions.subChargeTypeDropdownOptions[chargeType] ? prevState.dynamicOptions.subChargeTypeDropdownOptions[chargeType][0].value : '',
                    fspCountingFrom: FSPCountingDroOpts(operatingUnit).getFSPCountingFromByChargeType(chargeType).length > 0 ? FSPCountingDroOpts(operatingUnit).getFSPCountingFromByChargeType(chargeType)[0].value : '',
                    fspCountingTo: FSPCountingDroOpts(operatingUnit).getFSPCountingToByChargeType(chargeType).length > 0 ? FSPCountingDroOpts(operatingUnit).getFSPCountingToByChargeType(chargeType)[0].value : '',
                }
            }
        }))
    }

    const onFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any, operatingUnit?: string) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));

            if (fieldKey === "shipmentType" || fieldKey === "applyCalendarAdj"
                || fieldKey === "agentCode" || fieldKey === "forwarderCode"
            ) {
                val = val.join(",");
            }
        }
        if (fieldKey === 'chargeType') {
            // onChargeTypeChange(fieldValue, '');
            refershSubChargeType(val, operatingUnit ?? "");
        }

        masterFSPMainDispatch(prevState => {
            // if (fieldKey === 'graceHourFrom' || fieldKey === 'graceHourTo') {
            //     val.replace(/(^\+)|[^1-9\d]|(^0+)/g, '');
            //     if (val > 23 || val < 0) {
            //         return { ...prevState }
            //     }
            // }

            // if (fieldKey === 'graceMinuteFrom' || fieldKey === 'graceMinuteTo') {
            //     val.replace(/(^\+)|[^1-9\d]|(^0+)/g, '');
            //     if (val > 59 || val < 0) {
            //         return { ...prevState }
            //     }
            // }
            /*if (fieldKey === 'subChargeType') {
                onSubChargeTypeChange(prevState.masterState.editingEntity.chargeType ?? '', fieldValue);
            }
            if (fieldKey === 'chargeType' || fieldKey === 'subChargeType') {
                onChargeTypeChange(fieldKey === 'chargeType' ? val : prevState.masterState.editingEntity.chargeType,
                    fieldKey === 'subChargeType' ? val : prevState.masterState.editingEntity.subChargeType ?? '',
                    prevState.masterState.editingEntity.cntrMovement ?? '', operatingUnit ?? "");
            }*/
            return {
                ...prevState,
                forceRefresh: !prevState.forceRefresh,
                masterState: {
                    ...prevState.masterState,
                    editingEntity: {
                        ...prevState.masterState.editingEntity,
                        [fieldKey]: val,
                    },
                    allFormState: {
                        ...prevState.masterState.allFormState,
                        [fieldKey]: '',
                    }
                }
            }
        })
    }
    const onInitDefaultValue = (prevState: MasterFSPMaintenanceModel) => {
        return masterFSPMainDispatch({ ...onInitFormValue(prevState)/*, forceRefresh: !prevState.forceRefresh */ });
    }

    const onInitFormValue = (prevState: MasterFSPMaintenanceModel) => {
        const chargeTypeStr = prevState.masterState.editingEntity?.chargeType;
        const subChargeTypeStr = prevState.masterState.editingEntity?.subChargeType;

        const chargeTypeDropOpts = prevState.dynamicOptions.chargeTypeDropdownOptions;

        const chargeType = chargeTypeStr ? chargeTypeStr : (chargeTypeDropOpts && chargeTypeDropOpts.length > 0 ? chargeTypeDropOpts[0].value : "");
        const subChargeTypeDropOpts = chargeType ? prevState.dynamicOptions.subChargeTypeDropdownOptions[chargeType] : [];

        const subChargeType = subChargeTypeStr ? subChargeTypeStr : (subChargeTypeDropOpts && subChargeTypeDropOpts.length > 0 ? subChargeTypeDropOpts[0].value : "");

        const fspCalcMethodDroOpts = prevState.dynamicOptions.fspCalculateMethodDropdownOptions;
        const fspUomDroOpts = prevState.dynamicOptions.fspUomDropdownOptions;

        const fspCountingFromDroOpts = prevState.dynamicOptions.fspCountingFromDropdownOptions;
        const fspCountingToDroOpts = prevState.dynamicOptions.fspCountingToDropdownOptions;

        let defFspCalcMethod: string = "", defFspUom: string = "",
            defFspCountingFrm: string = "", defFspCountingTo: string = "",
            defFspCountingFrmVal: string = "", defFspCountingToVal: string = "",
            defFspDirection: string = "";
        if (fspCountingFromDroOpts && fspCountingFromDroOpts.length > 0) {
            defFspCountingFrm = fspCountingFromDroOpts[0].tagLabel;
            defFspCountingFrmVal = fspCountingFromDroOpts[0].value;
        }
        if (fspCountingToDroOpts && fspCountingToDroOpts.length > 0) {
            defFspCountingTo = fspCountingToDroOpts[0].tagLabel;
            defFspCountingToVal = fspCountingToDroOpts[0].value;
        }

        const directionDroOpts = prevState.dynamicOptions.directionDropdownOptions[(chargeType + "_" + subChargeType)];
        const tempDirectionDroOpts = prevState.dynamicOptions.directionDropdownOptions[(chargeType + "_")]
        const newDirectionDroOpts = _.unionBy(directionDroOpts, tempDirectionDroOpts, "value");

        if (newDirectionDroOpts && newDirectionDroOpts.length > 0) {
            defFspDirection = newDirectionDroOpts[0].value;
        }

        if (fspCalcMethodDroOpts && fspCalcMethodDroOpts.length > 0) {
            defFspCalcMethod = fspCalcMethodDroOpts[0].value
        }

        if (fspUomDroOpts && fspUomDroOpts.length > 0) {
            defFspUom = fspUomDroOpts[0].value
        }

        prevState.forceRefresh = !prevState.forceRefresh;
        prevState.masterState = {
            ...prevState.masterState,
            editingEntity: {
                ...prevState.masterState.editingEntity,
                chargeType: chargeType,
                subChargeType: subChargeType,
                fspCalculateMethod: defFspCalcMethod,
                fspUOM: defFspUom,
                fspCountingFrom: defFspCountingFrm,
                fspCountingFromDisVal: defFspCountingFrmVal,
                fspCountingToDisVal: defFspCountingToVal,
                fspCountingTo: defFspCountingTo,
                fspCountingDirection: defFspDirection,
                splitFirstDays: null,
                splitFirstDaysTo: null,
                splitRemainDaysTo: null
            },
            allFormState: {
                ...prevState.masterState.allFormState,
                fspCalculateMethod: '',
                fspUOM: '',
                fspCountingFrom: '',
                fspCountingTo: '',
                fspCountingDirection: '',
                splitFirstDays: '',
                splitFirstDaysTo: '',
                splitRemainDaysTo: ''
            }
        }

        return prevState;
    }

    const onChargeTypeChange = async (fieldKey: string, fieldValue: IFieldValue, operatingUnit: string, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        const chgType = val;
        masterFSPMainDispatch(prevState => {
            const subChgTypeOptions = chgType ? prevState.dynamicOptions.subChargeTypeDropdownOptions[chgType] : [];
            const subChgType = subChgTypeOptions && subChgTypeOptions.length > 0 ? subChgTypeOptions[0].value : "";

            //const directionDroOpts = initFspDirection(chgType, subChgType);
            const fspCalcMethodDroOpts = initFspCalculateMethod(chgType, subChgType);
            const fspUomDroOpts = initFspUom(chgType, subChgType, operatingUnit);

            const newChgType = getChargeTypeForCC(chgType, subChgType);
            const fspCountingFromDroOpts = initFspCountingFrom(newChgType, operatingUnit);
            const fspCountingToDroOpts = initFspCountingTo(newChgType, operatingUnit);
            const cntrMovementDroOpts = initCntrMovement(newChgType);
            const cntrStatusDroOpts = initCntrStatus(newChgType, "", operatingUnit);

            let defFspCalcMethod, defFspUom, defFspCountingFrm, defFspCountingFrmVal, defFspCountingTo, defFspCountingToVal, defFspDirection;
            if (fspCountingFromDroOpts && fspCountingFromDroOpts.length > 0) {
                defFspCountingFrm = fspCountingFromDroOpts[0].tagLabel;
                defFspCountingFrmVal = fspCountingFromDroOpts[0].value;
            }
            if (fspCountingToDroOpts && fspCountingToDroOpts.length > 0) {
                defFspCountingTo = fspCountingToDroOpts[0].tagLabel;
                defFspCountingToVal = fspCountingToDroOpts[0].value;
            }

            const directionDroOpts = prevState.dynamicOptions.directionDropdownOptions[(chgType + "_" + subChgType)];
            const tempDirectionDroOpts = prevState.dynamicOptions.directionDropdownOptions[(chgType + "_")]
            const newDirectionDroOpts = _.unionBy(directionDroOpts, tempDirectionDroOpts, "value");

            if (newDirectionDroOpts && newDirectionDroOpts.length > 0) {
                defFspDirection = newDirectionDroOpts[0].value;
            }

            if (fspCalcMethodDroOpts && fspCalcMethodDroOpts.length > 0) {
                defFspCalcMethod = fspCalcMethodDroOpts[0].value
            }

            if (fspUomDroOpts && fspUomDroOpts.length > 0) {
                defFspUom = fspUomDroOpts[0].value
            }

            const allFormState = prevState.masterState.allFormState || {};
            return {
                ...prevState,
                dynamicOptions: {
                    ...prevState.dynamicOptions,
                    //directionDropdownOptions: directionDroOpts,
                    fspCountingFromDropdownOptions: fspCountingFromDroOpts,
                    fspCountingToDropdownOptions: fspCountingToDroOpts,
                    cntrMovementDropdownOptions: cntrMovementDroOpts,
                    cntrStatusDropdownOptions: cntrStatusDroOpts,
                    fspCalcMethodDropdownOptions: fspCalcMethodDroOpts,
                    fspUomDropdownOptions: fspUomDroOpts
                },
                masterState: {
                    ...prevState.masterState,
                    editingEntity: {
                        ...prevState.masterState.editingEntity,
                        [fieldKey]: chgType,
                        subChargeType: subChgType ?? null,
                        fspCalculateMethod: defFspCalcMethod ?? null,
                        fspUOM: defFspUom ?? null,
                        fspCountingFrom: defFspCountingFrm ?? null,
                        fspCountingTo: defFspCountingTo ?? null,
                        fspCountingFromDisVal: defFspCountingFrmVal ?? null,
                        fspCountingToDisVal: defFspCountingToVal ?? null,
                        fspCountingDirection: defFspDirection ?? null,
                        splitFirstDays: null,
                        splitFirstDaysTo: null,
                        splitRemainDaysTo: null,
                    },
                    allFormState: {
                        ...prevState.masterState.allFormState,
                        [fieldKey]: "",
                        fspCountingToDisVal: defFspCountingTo ? "" : allFormState["fspCountingToDisVal"],
                        fspCountingFromDisVal: defFspCountingFrm ? "" : allFormState["fspCountingFromDisVal"],
                    }
                }
            }
        });
    }

    const initDropOptsByChg = (prevState: MasterFSPMaintenanceModel, chgType: string, subChgType: string, operatingUnit: string) => {
        const fspCalcMethodDroOpts = initFspCalculateMethod(chgType, subChgType);
        const fspUomDroOpts = initFspUom(chgType, subChgType, operatingUnit);

        const newChgType = getChargeTypeForCC(chgType, subChgType);
        let fspCountingFromDroOpts = initFspCountingFrom(newChgType, operatingUnit);
        let fspCountingToDroOpts = initFspCountingTo(newChgType, operatingUnit);
        if (subChgType && !("CC" === chgType || "DEM" === chgType)) {
            fspCountingFromDroOpts = initFspCountingFromBySubChgType(subChgType, operatingUnit);
            fspCountingToDroOpts = initFspCountingToBySubChgType(subChgType, operatingUnit);
        }

        const cntrMovementDroOpts = initCntrMovement(newChgType);
        const cntrStatusDroOpts = initCntrStatus(newChgType, "", operatingUnit);

        prevState.forceRefresh = !prevState.forceRefresh;
        prevState.dynamicOptions = {
            ...prevState.dynamicOptions,

            cntrMovementDropdownOptions: cntrMovementDroOpts,
            cntrStatusDropdownOptions: cntrStatusDroOpts,
            fspCountingFromDropdownOptions: fspCountingFromDroOpts,
            fspCountingToDropdownOptions: fspCountingToDroOpts,
            fspUomDropdownOptions: fspUomDroOpts,
            fspCalculateMethodDropdownOptions: fspCalcMethodDroOpts

        }

        return prevState;
    }

    const onFspCountingChange = (fieldKey: string, fieldValue: IFieldValue, operatingUnit: string, fFullValue?: any, dropKeyField?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        return masterFSPMainDispatch(prevState => {
            const chargeType = prevState.masterState.editingEntity.chargeType ?? "";
            const subChargeType = prevState.masterState.editingEntity.subChargeType ?? "";
            const newChageType = getChargeTypeForCC(chargeType, subChargeType);

            let dropKeyVal;
            if (dropKeyField === "fspCountingFrom") {
                dropKeyVal = FSPCountingDroOpts(operatingUnit).getFromKeyByValue(newChageType, val);
            }

            if (dropKeyField === "fspCountingTo") {
                dropKeyVal = FSPCountingDroOpts(operatingUnit).getToKeyByValue(newChageType, val);
            }

            return {
                ...prevState,
                masterState: {
                    ...prevState.masterState,
                    editingEntity: {
                        ...prevState.masterState.editingEntity,
                        [fieldKey]: val,
                        ...(dropKeyField && { [dropKeyField]: dropKeyVal })
                    },
                    allFormState: {
                        ...prevState.masterState.allFormState,
                        [fieldKey]: '',
                        ...(dropKeyField && { [dropKeyField]: "" })
                    }
                }
            }
        });
    }

    /*const initFspDirection = async (chargeType: string, subChargeType: string) => {
        let directionDropdownOption: DropdownProps[] = [];
        const data = await fspDirectionRefRepo.getAllFspDirectionRefComboBox();
        const newDataList = data.filter(entity => (!entity.chargeType || entity.chargeType === chargeType)
            && (!entity.subChargeType || entity.subChargeType === subChargeType));

        newDataList?.forEach(entity => {
            directionDropdownOption.push({
                dropdownLabel: entity.direction,
                tagLabel: entity.direction,
                value: entity.direction
            })
        })

        return directionDropdownOption;
    }*/

    const initFspCalculateMethod = (chargeType: string, subChargeType: string) => {
        return FspCalculateMethodDroOpts().getDroOptsModel(chargeType, subChargeType);
    }

    const initFspUom = (chargeType: string, subChargeType: string, operatingUnit: string) => {
        if ("RFH" === chargeType && "T1" === subChargeType) {
            return FSPUomDroOpts(operatingUnit).getFspUomModelForSpecialChg(chargeType, subChargeType);
        } else if ("RFH" === chargeType && ("PCI" === subChargeType || "PCX" === subChargeType)) {
            return FSPUomDroOpts(operatingUnit).getAllFspUomModel();
        } else {
            return FSPUomDroOpts(operatingUnit).getFspUomNoHfDayModel();
        }
    }

    const onSearchFieldChange = async (fieldKey: string, fieldValue: IFieldValue, fFullValue?: any) => {
        let val: any = fieldValue;
        if (_.isArray(val)) {
            val = _.uniq(val?.map((item: any) => item.value || item.key));
        }

        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                searchCriteria: {
                    ...prevState.searchCriteria,
                    [fieldKey]: val,
                }
            }
        })
    }
    const onDateRangeChange = (startDate: any, endDate: any, dateFields: { startField: string, endField: string }) => {
        masterFSPMainDispatch(prevState => ({
            ...prevState,
            searchCriteria: {
                ...prevState.searchCriteria,
                [dateFields.startField]: startDate,
                [dateFields.endField]: endDate,
            }
        }))
    }

    const onDateRangeChangeFroEdit = (startDate: any, endDate: any, dateFields: { startField: string, endField: string }) => {
        masterFSPMainDispatch(prevState => ({
            ...prevState,
            masterState: {
                ...prevState.masterState,
                editingEntity: {
                    ...prevState.masterState.editingEntity,
                    [dateFields.startField]: startDate,
                    [dateFields.endField]: endDate,
                }

            }
        }))
    }

    const onCancelDelete = () => {
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                isShowDeleteModal: false
            }
        });
    }

    const onDelete = async (selectedRows: MasterFSPEntity[]) => {
        return await repo.deleteEntity(selectedRows)
        // .then((res) => {
        //     masterFSPMainDispatch(prevState => {
        //         return {
        //             ...prevState,
        //             isShowRightPanel: false,
        //             currentSelectedRow: EMPTY_MASTER_FSP_ENTITY,
        //             selectedRows: [],
        //             isShowDeleteModal: false,

        //             masterState: {
        //                 isSliderOpen: false,
        //                 isTabularDataActive: false,
        //                 isAdd: false,
        //                 isRead: false,
        //                 isEditable: false,
        //                 isSaveClicked: false,
        //                 lastEditRowId: "",
        //                 isSelected: false,
        //                 slider: {
        //                     isAdd: false,
        //                     isRead: false,
        //                     isEditable: false
        //                 },
        //                 editingEntity: EMPTY_MASTER_FSP_ENTITY
        //             }
        //         }
        //     });
        // })
    }

    const updateSelectedRows = async (rows: MasterFSPEntity[]) => {
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                selectedRows: rows,
                forceRefresh: !prevState.forceRefresh
            }
        })
    }

    const onDeleteClick = () => {
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                isShowDeleteModal: true
            }
        });
    }

    const onApply = async (rows: MasterFSPEntity[]) => {
        return await repo.applyEntity(rows);
    }

    const onGroupButtonClick = async (buttonId: string) => {
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                selectedRows: [],
                currentSelectedRow: { ...EMPTY_MASTER_FSP_ENTITY },
            }
        })
        let isPendingApproval = false;
        let isRejected = false;
        let status = "";
        switch (buttonId) {
            case 'isPendingApproval':
                isPendingApproval = true;
                isRejected = false;
                status = "FINALIZED";
                break;
            case 'isRejected':
                isPendingApproval = false;
                isRejected = true;
                status = "NEW";
                break;
            default:
        }
        const pendingList = await onSearchPendingApproval();
        const rejectedList = await onSearchRejected();
        let newList: MasterFSPEntity[] = [];
        if (status === "FINALIZED") {
            newList = pendingList
        } else {
            newList = rejectedList;
        }
        newList = _.orderBy(newList, ["priority"], ['asc']);
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                selectedRows: [],
                // isAll: isAll,
                isPendingApproval: isPendingApproval,
                isRejected: isRejected,
                tableData: newList,
                searchStatus: buttonId,
                isAllowAutoSearch: false,
                isRejectedCount: _.isEmpty(rejectedList) ? 0 : rejectedList.length,
                isPendingApprovalCount: _.isEmpty(pendingList) ? 0 : pendingList.length,
                isSearch: !prevState.isSearch,

            }
        })
    }

    const onSubmit = async (rows: MasterFSPEntity[]) => {
        return await repo.submitEntity(rows);
    }

    const onRejectClick = (row: MasterFSPEntity) => {
        masterFSPMainDispatch(prevState => ({
            ...prevState,
            isShowRejectPad: true,
            currentSelectedRow: row
        }))
    };

    const onRejectReasonTextAreaChange = (fieldValue: IFieldValue) => {
        let val: any = fieldValue;
        return masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                currentSelectedRow: {
                    ...prevState.currentSelectedRow,
                    rejectReason: val
                }
            }
        });
    }

    const cancelRejectReasonModal = () => {
        masterFSPMainDispatch(prevState => ({
            ...prevState,
            isShowRejectPad: false,
            isRejecting: false,
            currentSelectedRow: {
                ...prevState.currentSelectedRow,
                rejectReason: prevState.rejectReason,
            }
        }))
    };

    const closeRejectReasonModal = () => {
        masterFSPMainDispatch(prevState => ({
            ...prevState,
            isShowRejectPad: false,
            isRejecting: true,
        }))
    };

    const onSearchClick = async () => {
        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                isShowCriteriaPanel: !prevState.isShowCriteriaPanel
            }
        });
    }

    const onReject = async (searchStatus: string, selectedRow: MasterFSPEntity, searchCriteria: MasterFSPEntity) => {
        closeRejectReasonModal();
        await repo.rejectEntity(selectedRow).then(async (data) => {
            if (searchStatus !== "") {
                await onGroupButtonClick(searchStatus);
            } else {
                await searchMasterFSP(searchCriteria);
            }
            masterFSPMainDispatch(prevState => ({
                ...prevState,
                isRejecting: false,
                rejectReason: ""
            }))
        });
    };

    const onApprove = async (rows: MasterFSPEntity[]) => {
        return await repo.approveEntity(rows);
    }

    const onSearchRejected = async () => {
        return await repo.getRejectedEntities();
    }

    const onSearchPendingApproval = async () => {
        return await repo.getPendingApprovalEntities();
    }

    const onCountRejectedPendingApproval = async () => {
        const [rejectedList, pendingApprovalList] = await Promise.all([
            repo.getRejectedEntities(),
            repo.getPendingApprovalEntities(),
        ]);

        masterFSPMainDispatch(prevState => {
            return {
                ...prevState,
                isPendingApprovalCount: _.isEmpty(pendingApprovalList) ? 0 : pendingApprovalList.length,
                isRejectedCount: _.isEmpty(rejectedList) ? 0 : rejectedList.length,
                selectedRows: [],
                isAllowAutoSearch: false,
                isBackMaster: false,
            }
        })

    }

    return {
        onDateRangeChange: onDateRangeChange,
        loadDropdownOption: loadDropdownOption,
        reLoadDropdownOption: reLoadDropdownOption,
        updateSelectedRows: updateSelectedRows,
        onAdd: onAdd,
        onEdit: onEdit,
        onReset: onReset,
        onClose: onClose,
        onSearch: onSearch,
        onSaveClicked: onSaveClicked,
        onSave: onSave,
        onCheckboxChange: onCheckboxChange,
        onFieldChange: onFieldChange,
        onEditClick: onEditClick,
        onCancelDelete: onCancelDelete,
        onDelete: onDelete,
        onDeleteClick: onDeleteClick,
        onApply: onApply,
        onShowLoading: onShowLoading,
        onHideLoading: onHideLoading,
        onGroupButtonClick: onGroupButtonClick,
        onSubmit: onSubmit,
        onApprove: onApprove,
        onRejectClick: onRejectClick,
        onRejectReasonTextAreaChange: onRejectReasonTextAreaChange,
        cancelRejectReasonModal: cancelRejectReasonModal,
        onReject: onReject,
        onSearchClick: onSearchClick,
        searchMasterFSP: searchMasterFSP,
        onSearchCriteriaResetClick: onSearchCriteriaResetClick,
        onSearchFieldChange: onSearchFieldChange,
        onDateRangeChangeFroEdit: onDateRangeChangeFroEdit,
        onSearchRejected: onSearchRejected,
        onSearchPendingApproval: onSearchPendingApproval,
        onCountRejectedPendingApproval: onCountRejectedPendingApproval,
        onChargeTypeChange: onChargeTypeChange,
        onSubChargeTypeChange: onSubChargeTypeChange,
        onFspCountingChange: onFspCountingChange,
        onInitDefaultValue: onInitDefaultValue
    }
}